import type { AnswerRecord } from "../types/index.js";

const DAY = 86_400_000; // 1 day in ms

function reviewInterval(streak: number): number {
  switch (streak) {
    case 1:
      return 1 * DAY;
    case 2:
      return 3 * DAY;
    case 3:
      return 7 * DAY;
    case 4:
      return 14 * DAY;
    default:
      return 30 * DAY;
  }
}

export interface QuestionTrackerState {
  bookmarkedIDs: Set<string>;
  wrongIDs: Set<string>;
  correctIDs: Set<string>;
  answerRecords: Map<string, AnswerRecord>;
}

export class QuestionTracker {
  bookmarkedIDs: Set<string>;
  wrongIDs: Set<string>;
  correctIDs: Set<string>;
  answerRecords: Map<string, AnswerRecord>;
  private namespace: string;
  private onChange: (() => void) | null = null;

  constructor(namespace: string) {
    this.namespace = namespace;
    this.bookmarkedIDs = this.loadSet("bookmarkedIDs");
    this.wrongIDs = this.loadSet("wrongIDs");
    this.correctIDs = this.loadSet("correctIDs");
    this.answerRecords = this.loadRecords();
  }

  setOnChange(cb: () => void) {
    this.onChange = cb;
  }

  private notify() {
    this.onChange?.();
  }

  private key(suffix: string): string {
    return `${this.namespace}:${suffix}`;
  }

  private loadSet(suffix: string): Set<string> {
    try {
      const raw = localStorage.getItem(this.key(suffix));
      if (!raw) return new Set();
      return new Set(JSON.parse(raw) as string[]);
    } catch {
      return new Set();
    }
  }

  private saveSet(suffix: string, set: Set<string>) {
    localStorage.setItem(this.key(suffix), JSON.stringify([...set]));
  }

  private loadRecords(): Map<string, AnswerRecord> {
    try {
      const raw = localStorage.getItem(this.key("answerRecords"));
      if (!raw) return new Map();
      const obj = JSON.parse(raw) as Record<string, AnswerRecord>;
      return new Map(Object.entries(obj));
    } catch {
      return new Map();
    }
  }

  private saveRecords() {
    const obj: Record<string, AnswerRecord> = {};
    this.answerRecords.forEach((v, k) => {
      obj[k] = v;
    });
    localStorage.setItem(this.key("answerRecords"), JSON.stringify(obj));
  }

  toggleBookmark(id: string) {
    if (this.bookmarkedIDs.has(id)) {
      this.bookmarkedIDs.delete(id);
    } else {
      this.bookmarkedIDs.add(id);
    }
    this.saveSet("bookmarkedIDs", this.bookmarkedIDs);
    this.notify();
  }

  isBookmarked(id: string): boolean {
    return this.bookmarkedIDs.has(id);
  }

  recordWrongAnswer(id: string) {
    this.wrongIDs.add(id);
    this.saveSet("wrongIDs", this.wrongIDs);
    const record = this.answerRecords.get(id);
    if (record) {
      record.consecutiveCorrect = 0;
      this.saveRecords();
    }
    this.notify();
  }

  clearWrongAnswer(id: string) {
    this.wrongIDs.delete(id);
    this.saveSet("wrongIDs", this.wrongIDs);
    this.notify();
  }

  recordCorrectAnswer(id: string) {
    this.correctIDs.add(id);
    this.saveSet("correctIDs", this.correctIDs);
    const existing = this.answerRecords.get(id);
    if (existing) {
      existing.lastCorrectDate = Date.now();
      existing.consecutiveCorrect += 1;
    } else {
      this.answerRecords.set(id, {
        lastCorrectDate: Date.now(),
        consecutiveCorrect: 1,
      });
    }
    this.saveRecords();
    this.notify();
  }

  get dueForReviewIDs(): Set<string> {
    const now = Date.now();
    const result = new Set<string>();
    this.answerRecords.forEach((record, id) => {
      if (record.consecutiveCorrect <= 0) return;
      const interval = reviewInterval(record.consecutiveCorrect);
      if (now >= record.lastCorrectDate + interval) {
        result.add(id);
      }
    });
    return result;
  }

  getState(): QuestionTrackerState {
    return {
      bookmarkedIDs: new Set(this.bookmarkedIDs),
      wrongIDs: new Set(this.wrongIDs),
      correctIDs: new Set(this.correctIDs),
      answerRecords: new Map(this.answerRecords),
    };
  }
}
